import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLE_HOME_ROUTES } from "../../constants/roles";
import imagineHerLogo from "../../assets/Imagine-Her-Logo-.svg";
import "./signin.css";

const AUTH_FLASH_KEY = "ih_auth_flash";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const flashMessage = sessionStorage.getItem(AUTH_FLASH_KEY);
    if (!flashMessage) return;

    setNotice(flashMessage);
    sessionStorage.removeItem(AUTH_FLASH_KEY);
  }, []);

  // If ProtectedRoute bounced us here, it stashed the original destination
  // in location.state.from — we'll send the user back there after login.
  const from = location.state?.from?.pathname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { user, error: loginError } = await login(email, password);
      if (user) {
        // Prefer the originally requested route; otherwise fall back to the
        // role's home workspace so each role lands in the right place.
        const target = from || ROLE_HOME_ROUTES[user.role] || "/";
        navigate(target, { replace: true });
      } else {
        setError(loginError || "Invalid email or password. Please try again.");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="signin-container">
        <div className="signin-header">
          <div className="signin-logo">
            <img
              src={imagineHerLogo}
              alt="Imagine Her"
              className="signin-logo-image"
            />
          </div>
          {/* <h1 className="signin-title">Imagine Her</h1> */}
          <div className="signin-subtitle">ASPIRE</div>
          <div className="signin-subtitle">
            Analytics System portfolio Impact Report Evidence
          </div>
        </div>

        <div className="signin-card">
          <div className="signin-card-header">
            <h2 className="signin-card-title">Sign in</h2>
            <p className="signin-card-description">
              Enter your credentials to access the platform
            </p>
          </div>

          <div className="signin-card-content">
            <form onSubmit={handleSubmit} className="signin-form">
              {notice && (
                <div className="signin-alert signin-alert-info" role="status">
                  <p>{notice}</p>
                </div>
              )}

              {error && (
                <div
                  className="signin-alert signin-alert-destructive"
                  role="alert"
                >
                  <p>{error}</p>
                </div>
              )}

              <div className="signin-field">
                <label htmlFor="email" className="signin-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="signin-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="signin-field">
                <label htmlFor="password" className="signin-label">
                  Password
                </label>
                <div className="signin-password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="signin-input signin-input-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="signin-password-toggle"
                    onClick={() => setShowPassword((shown) => !shown)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="signin-icon" aria-hidden="true" />
                    ) : (
                      <Eye className="signin-icon" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div className="signin-metaRow">
                <Link to="/forgot-password" className="signin-link">
                  Forgot your password?
                </Link>
              </div>

              <button
                type="submit"
                className="signin-button signin-button-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="signin-spinner" aria-hidden="true" />
                ) : (
                  <>
                    <LogIn
                      className="signin-icon signin-icon-sm"
                      aria-hidden="true"
                    />
                    <span>Sign in</span>
                  </>
                )}
              </button>
            </form>
            <div className="signin-demo">
              <p className="signin-demo-title">Access Note</p>
              <div className="signin-demo-list">
                <p>Contact Admin for an account.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
